import React, { createContext, useState, useEffect, useRef, useCallback } from 'react';
import { API_URL } from '../config';

export const AuthContext = createContext(null);

// ── Clé localStorage ─────────────────────────────────────────
const REFRESH_TOKEN_KEY = 'darna-immo_refresh_token';

export function AuthProvider({ children }) {
  const [user, setUser]               = useState(null);
  const [accessToken, setAccessToken] = useState(null);
  const [isLoading, setIsLoading]     = useState(true);

  // Ref sur l'accessToken pour éviter les problèmes de closure périmée dans authFetch
  const accessTokenRef = useRef(null);
  // Ref pour éviter les refreshs simultanés (double-tap protection)
  const refreshingRef  = useRef(false);
  const refreshPromise = useRef(null);

  const isAuthenticated = !!user && !!accessToken;

  // Synchroniser la ref à chaque changement
  useEffect(() => {
    accessTokenRef.current = accessToken;
  }, [accessToken]);

  // ── Refresh silencieux au montage ────────────────────────────
  useEffect(() => {
    const storedRefresh = localStorage.getItem(REFRESH_TOKEN_KEY);
    if (storedRefresh) {
      refreshSession(storedRefresh).finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, []);

  // ── Renouvellement de session ────────────────────────────────
  const refreshSession = useCallback(async (refreshToken) => {
    // Éviter les appels simultanés (si plusieurs composants déclenchent un refresh en même temps)
    if (refreshingRef.current) {
      return refreshPromise.current;
    }

    refreshingRef.current = true;
    refreshPromise.current = (async () => {
      try {
        const res = await fetch(`${API_URL}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        });

        if (!res.ok) throw new Error('Refresh échoué');

        const data = await res.json();
        setUser(data.user);
        setAccessToken(data.accessToken);
        accessTokenRef.current = data.accessToken;
        localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
        return data.accessToken;
      } catch {
        // Session expirée ou révoquée → déconnecter
        localStorage.removeItem(REFRESH_TOKEN_KEY);
        setUser(null);
        setAccessToken(null);
        accessTokenRef.current = null;
        return null;
      } finally {
        refreshingRef.current = false;
        refreshPromise.current = null;
      }
    })();

    return refreshPromise.current;
  }, []);

  // ── Connexion ────────────────────────────────────────────────
  const login = async (email, password) => {
    const res = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    const data = await res.json();
    if (!res.ok) {
      const err = new Error(data.erreur || 'Erreur de connexion');
      err.verrouille           = data.verrouille           || false;
      err.minutes_restantes    = data.minutes_restantes    || null;
      err.avertissement        = data.avertissement        || null;
      err.tentatives_restantes = data.tentatives_restantes || null;
      err.statusCode           = res.status;
      throw err;
    }

    setUser(data.user);
    setAccessToken(data.accessToken);
    accessTokenRef.current = data.accessToken;
    localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
    return data;
  };

  // ── Inscription ──────────────────────────────────────────────
  const register = async (userData) => {
    const res = await fetch(`${API_URL}/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(userData),
    });

    const data = await res.json();
    if (!res.ok) {
      const err = new Error(data.erreur || "Erreur d'inscription");
      err.exigences = data.exigences || null; // Exigences de mot de passe
      throw err;
    }

    setUser(data.user);
    setAccessToken(data.accessToken);
    accessTokenRef.current = data.accessToken;
    localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
    return data;
  };

  // ── Déconnexion ──────────────────────────────────────────────
  const logout = async () => {
    const currentToken = accessTokenRef.current;
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);

    // Nettoyage local en premier (même si le serveur ne répond pas)
    setUser(null);
    setAccessToken(null);
    accessTokenRef.current = null;
    localStorage.removeItem(REFRESH_TOKEN_KEY);

    // Révoquer le refresh token côté serveur (best effort)
    if (currentToken && refreshToken) {
      try {
        await fetch(`${API_URL}/auth/logout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${currentToken}`,
          },
          body: JSON.stringify({ refreshToken }),
        });
      } catch {
        // Ignorer: le token expirera naturellement
      }
    }
  };

  // ── Changement de mot de passe ────────────────────────────────
  const changePassword = async (currentPassword, newPassword) => {
    const res = await fetch(`${API_URL}/auth/change-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${accessTokenRef.current}`,
      },
      body: JSON.stringify({ currentPassword, newPassword }),
    });

    const data = await res.json();
    if (!res.ok) {
      const err = new Error(data.erreur || 'Erreur changement mot de passe');
      err.exigences = data.exigences || null;
      throw err;
    }

    // Mettre à jour les tokens (toutes les autres sessions ont été révoquées)
    setAccessToken(data.accessToken);
    accessTokenRef.current = data.accessToken;
    localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken);
    return data;
  };

  const updateUser = (updatedUser) => setUser(updatedUser);

  // ── Fetch authentifié (avec refresh automatique) ─────────────
  // Utilise une ref pour toujours avoir le token le plus récent.
  const authFetch = useCallback(async (url, options = {}) => {
    const headers = {
      ...options.headers,
    };

    // Ne pas écraser Content-Type pour les FormData (multipart)
    if (!(options.body instanceof FormData)) {
      headers['Content-Type'] = headers['Content-Type'] || 'application/json';
    }

    if (accessTokenRef.current) {
      headers['Authorization'] = `Bearer ${accessTokenRef.current}`;
    }

    let res = await fetch(url, { ...options, headers });

    // Si 401, tenter un refresh (token expiré)
    if (res.status === 401) {
      const storedRefresh = localStorage.getItem(REFRESH_TOKEN_KEY);
      if (storedRefresh) {
        const newToken = await refreshSession(storedRefresh);
        if (newToken) {
          // Réessayer avec le nouveau token
          headers['Authorization'] = `Bearer ${newToken}`;
          res = await fetch(url, { ...options, headers });
        }
      }
    }

    return res;
  }, [refreshSession]); // Dépendance stable (refreshSession est mémoïsée)

  return (
    <AuthContext.Provider value={{
      user,
      accessToken,
      isAuthenticated,
      isLoading,
      login,
      register,
      logout,
      changePassword,
      updateUser,
      authFetch,
    }}>
      {children}
    </AuthContext.Provider>
  );
}
